import { useTheme } from '@mui/material/styles';
import type { RouteComponentProps } from '@reach/router';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import Attachments from '@components/Attachments';
import { DrilldownHeader } from '@components/DrilldownHeader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import { ListItem, ListItemContainer } from '@components/ListItem';
import RelatedPersonModalContent from '@components/Modal/Content/ContactModalContent';

import { LEGAL } from '@constants/routes';

import { useModal } from '@hooks/useModal';
import { useRelatedPersons } from '@hooks/useRelatedPersons';
import { useSession } from '@hooks/useSession';

import AppLayout from '@layouts/AppLayout';
import { MainContainer } from '@layouts/DrilldownStyle';

import type { RelatedPersonRecord } from '@models/related-person';

const messages = defineMessages({
  title: {
    id: 'livingWill.title',
    defaultMessage: 'Living Will / Power of Attorney for Personal Care',
  },
  subtitle: {
    id: 'livingWill.subtitle',
    defaultMessage: 'Upload your legal documents here',
  },
  documents: {
    id: 'livingWill.documents',
    defaultMessage: 'Documents',
  },
  powerOfAttorney: {
    id: 'livingWill.powerOfAttorney',
    defaultMessage: 'Power of attorney',
  },
  legalCounsel: {
    id: 'livingWill.legalCounsel',
    defaultMessage: 'Legal Counsel',
  },
  noAttachments: {
    id: 'livingWill.noAttachments',
    defaultMessage: 'No Attachments',
  },
  noLegalCounsel: {
    id: 'livingWill.noLegalCounsel',
    defaultMessage: 'No Legal Counsel Provided',
  },
  noPoa: {
    id: 'livingWill.noPoa',
    defaultMessage: 'No POA Provided',
  },
});

const LivingWill: React.FC<RouteComponentProps> = () => {
  const { formatMessage } = useIntl();
  const {
    state: { activeProfile },
  } = useSession();
  // Related Persons are fetched using active profile
  const patient_id = activeProfile?.patient_id;

  const { data, loading } = useRelatedPersons();

  const { palette } = useTheme();

  const { closeModal, openModal, setContent } = useModal();
  const { powerOfAttorney, legalCounsel } = data;

  const attachments =
    powerOfAttorney?.attachments.concat(legalCounsel?.attachments ?? []) ?? [];

  const handleOpenModal = (type: string, person?: RelatedPersonRecord) => {
    openModal({
      width: 500,
      props: {
        height: '300px',
      },
      children: null,
    });
    setContent({
      children: (
        <RelatedPersonModalContent
          onClose={closeModal}
          person={person}
          type={type}
        />
      ),
    });
  };

  const poaProps = {
    description: powerOfAttorney?.primaryAddress,
    subtitle: powerOfAttorney?.phone ?? powerOfAttorney?.email ?? '',
    subtitleColor: palette.text.secondary,
  };

  const legalProps = {
    description: legalCounsel?.primaryAddress,
    subtitle: legalCounsel?.phone ?? legalCounsel?.email ?? '',
    subtitleColor: palette.text.secondary,
  };

  return (
    <AppLayout friendlyName={formatMessage(messages.title)} loading={loading}>
      <DrilldownHeader
        backRoute={LEGAL}
        title={formatMessage(messages.title)}
      />
      <MainContainer maxWidth="600px">
        <ListItemContainer title={formatMessage(messages.documents)}>
          {attachments.length > 0 ? (
            attachments.map((attachment) => {
              return (
                <ListItem key={attachment.title}>
                  <Attachments
                    patient_id={patient_id}
                    attachments={[attachment]}
                    title=" "
                  />
                </ListItem>
              );
            })
          ) : (
            <ListItem title={formatMessage(messages.noAttachments)} />
          )}
        </ListItemContainer>

        <ListItemContainer title={formatMessage(messages.powerOfAttorney)}>
          <ListItem
            icon={powerOfAttorney ? 'pencil' : 'plus'}
            onClick={() => handleOpenModal('POWATT', powerOfAttorney)}
            title={powerOfAttorney?.name ?? formatMessage(messages.noPoa)}
            {...(powerOfAttorney?.name ? poaProps : {})}
          />
        </ListItemContainer>

        <ListItemContainer title={formatMessage(messages.legalCounsel)}>
          <ListItem
            icon={legalCounsel ? 'pencil' : 'plus'}
            // TODO: Code for legal counsel
            onClick={() => handleOpenModal('legal-counsel', legalCounsel)}
            title={legalCounsel?.name ?? formatMessage(messages.noLegalCounsel)}
            {...(legalCounsel?.name ? legalProps : {})}
          />
        </ListItemContainer>
      </MainContainer>
    </AppLayout>
  );
};

export default withErrorBoundary(LivingWill, {
  FallbackComponent: (props) => {
    const { formatMessage } = useIntl();

    return (
      <AppLayout friendlyName={formatMessage(messages.title)}>
        <DrilldownHeader
          backRoute={LEGAL}
          title={formatMessage(messages.title)}
        />
        <MainContainer maxWidth="600px">
          <ErrorBoundaryFallback {...props} />
        </MainContainer>
      </AppLayout>
    );
  },
});
